/**
 * ONE RULE FOR "IS THIS DANCER'S COSTUME PICKED UP" (Bobbie, 2026-09-25: "The
 * costumes need to work correctly so each qr code is scanable and marks of on
 * the dancers view os costumes are still waiting to be picked up and what has
 * been pciked up").
 *
 * A costume card (costumes/{id}) belongs to a CLASS. Pickup belongs to a
 * DANCER: costumes/{id}.pickups.{studentId} = { at, byUid, via }, written only
 * by netlify/functions/costumePickup.mjs. This file is the single reading of
 * that record, shared by the scan page, the staff board and the Function
 * itself (lib/costumePickupCore.mjs imports it), so the phone and the server
 * can never disagree about who is waiting.
 *
 * PURE. No React, no Firebase, and no extensionless local import: Node's type
 * stripping runs it directly in tests and inside the Netlify Function.
 *
 * THREE SETTINGS BOBBIE HAS NOT RULED ON YET. Each is one line below, and each
 * is read by the Function, so changing it here changes who the server lets
 * through, not only what the screen offers:
 *   - COSTUME_PICKUP_DESK_ROLES: who marks at the front desk (Question 1).
 *   - COSTUME_PICKUP_PARENTS_MAY_MARK: whether a linked parent may mark on her
 *     own phone once the card is ready (Question 1).
 *   - isCostumeTrackedForPickup: which cards are tracked at all (Question 3).
 */

/** How a pickup was recorded: a linked grown-up on her own phone, or the desk. */
export type CostumePickupVia = 'family' | 'desk';

/**
 * QUESTION 1, THE DESK HALF. Active Director, Manager and Admin (foh): the
 * three roles that already track costume orders under R15 (DECISIONS.md,
 * Bobbie by name 2026-08-13). Teachers are left off until she rules. Adding a
 * role here is the whole change.
 */
export const COSTUME_PICKUP_DESK_ROLES: readonly string[] = ['director', 'manager', 'foh'];

/**
 * QUESTION 1, THE PARENT HALF. A grown-up linked to the dancer (her
 * familyUids) may mark her own dancer's bag once the studio has marked the
 * card ready. That is the power linked parents already held before this
 * change, narrowed to their own dancer. false makes pickup desk-only.
 */
export const COSTUME_PICKUP_PARENTS_MAY_MARK = true;

/** The costume fields this module reads. Plain data, so the server's raw
 *  Firestore records and the app's normalized CostumeDoc both fit. */
export interface CostumePickupCard {
  approvalStatus?: string | null;
  provisionType?: string | null;
  orderStatus?: string | null;
  readyForPickup?: boolean | null;
  pickups?: unknown;
  /** Roster dancers Kim switched off this card (costumeRecords.mjs writes it). */
  notWornBy?: unknown;
  /** Kim's own arrived answer per dancer, on a family's copy only. */
  arrivedByDancer?: Record<string, boolean>;
}

/**
 * WHO WEARS THIS CARD (2026-11-02). A class can carry a boys and a girls
 * costume; Kim switches each dancer off the card that is not theirs in the
 * Costume report, and the server lists them on costumes/{id}.notWornBy. Every
 * count against a roster (labels, the pickup report, "the whole class has it")
 * counts only the dancers who wear the card.
 */
export function costumeNotWornBy(card: Pick<CostumePickupCard, 'notWornBy'>): Set<string> {
  return new Set(Array.isArray(card.notWornBy) ? card.notWornBy.filter((id): id is string => typeof id === 'string' && !!id) : []);
}

export function wearsCostume(card: Pick<CostumePickupCard, 'notWornBy'>, studentId: string): boolean {
  return !costumeNotWornBy(card).has(studentId);
}

/** The roster, less the dancers switched off this card. */
export function costumeWearers(card: Pick<CostumePickupCard, 'notWornBy'>, rosterStudentIds: readonly string[]): string[] {
  const off = costumeNotWornBy(card);
  return rosterStudentIds.filter((id) => !off.has(id));
}

export interface CostumePickupEntry {
  at: unknown;
  byUid: string | null;
  via: CostumePickupVia | null;
}

/**
 * QUESTION 3. Which cards are tracked for pickup: only the ones marked "Studio
 * is ordering primary costume". A family-supplied card has nothing to hand out,
 * and a card left at "Not set yet" is not known to be coming from the studio,
 * so neither gets labels or a pickup button. To also track "Not set yet"
 * cards, return `card.provisionType !== 'family-supplied'` instead.
 */
export function isCostumeTrackedForPickup(card: Pick<CostumePickupCard, 'provisionType'>): boolean {
  return card.provisionType === 'studio-ordering';
}

/** A card with no approvalStatus predates approvals and counts as approved. */
export function isCostumeApprovedForPickup(card: Pick<CostumePickupCard, 'approvalStatus'>): boolean {
  return card.approvalStatus == null || card.approvalStatus === 'approved';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * True once the card tracks pickup per dancer: the Function has written a
 * pickups map to it, even an empty one (an Undo of the last dancer, or a
 * Reopen, leaves `{}` so the card never falls back to whole-class reading).
 */
export function hasPerDancerPickups(card: Pick<CostumePickupCard, 'pickups'>): boolean {
  return isRecord(card.pickups);
}

/**
 * An OLD card: marked picked up for the whole class by the family tap that
 * existed before 2026-09-25, with no per-dancer map. Every dancer on it reads
 * as picked up, labelled "whole class", until the desk reopens it.
 */
export function isWholeClassPickedUp(card: Pick<CostumePickupCard, 'orderStatus' | 'pickups'>): boolean {
  return card.orderStatus === 'picked-up' && !hasPerDancerPickups(card);
}

/** This dancer's own pickup record, or null. Never throws on a malformed map. */
export function pickupEntryFor(card: Pick<CostumePickupCard, 'pickups'>, studentId: string): CostumePickupEntry | null {
  if (!studentId || !isRecord(card.pickups)) return null;
  const raw = card.pickups[studentId];
  if (!isRecord(raw)) return null;
  return {
    at: raw.at ?? null,
    byUid: typeof raw.byUid === 'string' && raw.byUid ? raw.byUid : null,
    via: raw.via === 'family' || raw.via === 'desk' ? raw.via : null,
  };
}

/**
 * WHO WALKED OUT WITH THE BAG (2026-11-03): the card holds the KIND only, so
 * every staff reader of the card (Jr Staff included) learns nothing about
 * another family. The grown-up and the desk's note are on the server-only
 * dancer record; both halves are documented once, in
 * netlify/functions/lib/costumePickupCore.mjs. Older records carry none.
 */
export type CostumePickupCollectorKind = 'guardian' | 'dancer' | 'other';
export interface CostumePickupCollector { kind: CostumePickupCollectorKind }

/** This dancer's collectedBy kind on the card, or null. Never throws on a malformed record. */
export function pickupCollectorFor(card: Pick<CostumePickupCard, 'pickups'>, studentId: string): CostumePickupCollector | null {
  if (!studentId || !isRecord(card.pickups)) return null;
  const raw = card.pickups[studentId];
  if (!isRecord(raw) || !isRecord(raw.collectedBy)) return null;
  const kind = raw.collectedBy.kind;
  return kind === 'guardian' || kind === 'dancer' || kind === 'other' ? { kind } : null;
}

export type CostumePickupState =
  | { kind: 'picked-up'; at: unknown; byUid: string | null; via: CostumePickupVia | null; wholeClass: boolean }
  | { kind: 'ready' }
  | { kind: 'not-ready' }
  | { kind: 'family-provides' }
  | { kind: 'check-with-studio' };

/**
 * Where ONE dancer's costume stands, in the order a person would ask it:
 *   1. her own pickup record, if the desk or her grown-up marked it;
 *   2. a costume the family provides, which has nothing to pick up. This
 *      comes BEFORE an old class flag: the tap that existed before 2026-09-25
 *      and the Tracker's whole-class button could both stamp "picked up" on a
 *      family-supplied card, and the family screens always said "You provide"
 *      for it, so they still do;
 *   3. an old whole-class "picked up", which covers everyone on the card;
 *   4. a card not approved or not tracked for pickup: check with the studio;
 *   5. otherwise ready (the studio marked it ready) or not ready yet.
 * A per-dancer card whose class flag reads "picked up" while THIS dancer has
 * no record (she joined after the rest were handed out) is still waiting: the
 * class flag never speaks for a dancer with her own map entry missing.
 */
export function pickupStateFor(card: CostumePickupCard, studentId: string): CostumePickupState {
  const entry = pickupEntryFor(card, studentId);
  if (entry) return { kind: 'picked-up', ...entry, wholeClass: false };
  if (card.provisionType === 'family-supplied') return { kind: 'family-provides' };
  if (isWholeClassPickedUp(card)) return { kind: 'picked-up', at: null, byUid: null, via: null, wholeClass: true };
  if (!isCostumeApprovedForPickup(card) || !isCostumeTrackedForPickup(card)) return { kind: 'check-with-studio' };
  // Kim's own "not arrived" for this one dancer (a back order) holds her bag
  // back even when the class card says ready (family copy: arrivedByDancer).
  if (card.arrivedByDancer?.[studentId] === false) return { kind: 'not-ready' };
  return card.readyForPickup === true ? { kind: 'ready' } : { kind: 'not-ready' };
}

export interface ClassPickupProgress {
  /** Dancers on the class's CURRENT roster. */
  expected: number;
  /** Of those, how many are picked up (all of them on an old whole-class card). */
  pickedUp: number;
  /** Every current dancer is picked up, and there is at least one. */
  allPickedUp: boolean;
  /** The count comes from an old whole-class mark, not per-dancer records. */
  wholeClass: boolean;
}

/** How much of a class card has gone home, counted against its current roster. */
export function classPickupProgress(card: CostumePickupCard, rosterStudentIds: readonly string[]): ClassPickupProgress {
  const roster = costumeWearers(card, Array.from(new Set(rosterStudentIds.filter((id) => typeof id === 'string' && id))));
  if (isWholeClassPickedUp(card)) {
    return { expected: roster.length, pickedUp: roster.length, allPickedUp: true, wholeClass: true };
  }
  const pickedUp = roster.filter((id) => pickupEntryFor(card, id) !== null).length;
  return { expected: roster.length, pickedUp, allPickedUp: roster.length > 0 && pickedUp === roster.length, wholeClass: false };
}

/**
 * The ONE word for a card as a whole, for a screen that speaks for the class
 * rather than for one dancer: a staff view of the class page, and the
 * Helper's comp-day card, which holds one status per dance for every family.
 *   'picked-up'  every dancer on the CURRENT roster has her own record (or an
 *                old card with no map was marked picked up for the class);
 *   'ready'      the studio marked it ready and someone is still waiting;
 *   'waiting'    not ready yet.
 * On a per-dancer card the map, counted against the roster, is the truth and
 * the class flag is not: that flag can still read "picked up" after a new
 * dancer joined, and can read "arrived" after the Tracker's Mark arrived
 * even though every bag went home.
 */
export type ClassPickupPhase = 'picked-up' | 'ready' | 'waiting';

export function classPickupPhase(card: CostumePickupCard, rosterStudentIds: readonly string[]): ClassPickupPhase {
  if (hasPerDancerPickups(card)) {
    if (classPickupProgress(card, rosterStudentIds).allPickedUp) return 'picked-up';
  } else if (card.orderStatus === 'picked-up') {
    return 'picked-up';
  }
  return card.readyForPickup === true ? 'ready' : 'waiting';
}
